import React, { createContext, useState, useCallback, useContext } from 'react';
import Toast from '../components/Toast/Toast';
import './../components/Toast/Toast.css';

// ===== CONTEXT =====
const ToastContext = createContext();
let toastId = 0;

// ===== TOAST CONTAINER =====
const ToastContainer = ({ toasts, removeToast }) => {
  if (!toasts || toasts.length === 0) return null;
  return (
    <div className="toast-container">
      {toasts.map((toast) => (
        <Toast
          key={toast.id}
          id={toast.id}
          type={toast.type}
          message={toast.message}
          duration={toast.duration || 4000}
          onClose={removeToast}
        />
      ))}
    </div>
  );
};

// ===== PROVIDER =====
export const ToastProvider = ({ children }) => {
  const [toasts, setToasts] = useState([]);

  const showToast = useCallback((type, message, duration = 2000) => {
    const id = ++toastId;
    setToasts((prev) => [...prev, { id, type, message, duration }]);
    return id;
  }, []);

  const removeToast = useCallback((id) => {
    setToasts((prev) => prev.filter((toast) => toast.id !== id));
  }, []);

  const clearToasts = useCallback(() => setToasts([]), []);

  return (
    <ToastContext.Provider value={{ showToast, removeToast, clearToasts }}>
      {children}
      <ToastContainer toasts={toasts} removeToast={removeToast} />
    </ToastContext.Provider>
  );
};

// ===== HOOK =====
export const useToast = () => {
  const context = useContext(ToastContext);
  if (!context) throw new Error('useToast must be used within a ToastProvider');
  
  const { showToast, removeToast, clearToasts } = context;
  
  return {
    showToast,
    removeToast,
    clearToasts,
    showSuccess: (message, duration) => showToast('success', message, duration),
    showError: (message, duration) => showToast('error', message, duration),
    showWarning: (message, duration) => showToast('warning', message, duration),
    showInfo: (message, duration) => showToast('info', message, duration),
  };
};

// ===== DEFAULT EXPORT (for backward compatibility) =====
export default useToast;